import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  LayoutDashboard,
  BookOpen,
  User,
  Settings,
  History,
  Wrench,
  Network,
  X,
} from 'lucide-react';
import type { AdminWorldDetail, AdminWorldState } from '../../../lib/api';
import { WorldHeader } from './WorldHeader';

export type WorldSection = 'overview' | 'canon' | 'graph' | 'correspondent' | 'configuration' | 'history' | 'operations';

interface WorldSidebarProps {
  world: AdminWorldDetail;
  state: AdminWorldState | null;
  versions: Array<{ id: string; version: number; updatedAt: string }>;
  currentVersion: number | null;
  onVersionChange: (version: number | null) => void;
  activeSection: WorldSection;
  onSectionChange: (section: WorldSection) => void;
  isOpen: boolean;
  onClose: () => void;
  onThumbnailClick?: () => void;
}

const sections: Array<{ id: WorldSection; label: string; icon: typeof LayoutDashboard }> = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'canon', label: 'Canon', icon: BookOpen },
  { id: 'graph', label: 'Graph', icon: Network },
  { id: 'correspondent', label: 'Correspondent', icon: User },
  { id: 'configuration', label: 'Configuration', icon: Settings },
  { id: 'history', label: 'History', icon: History },
  { id: 'operations', label: 'Operations', icon: Wrench },
];

export function WorldSidebar({
  world,
  state,
  versions,
  currentVersion,
  onVersionChange,
  activeSection,
  onSectionChange,
  isOpen,
  onClose,
  onThumbnailClick,
}: WorldSidebarProps) {
  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static w-72 h-screen bg-void border-r border-stone/30 flex flex-col z-50 lg:z-auto transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Back navigation */}
        <div className="flex items-center justify-between p-3 border-b border-stone/30">
          <Link
            to="/admin/worlds"
            className="flex items-center gap-2 text-sm text-mist hover:text-parchment transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Worlds</span>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 text-mist hover:text-parchment hover:bg-stone/30 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* World Header */}
        <WorldHeader
          world={world}
          state={state}
          versions={versions}
          currentVersion={currentVersion}
          onVersionChange={onVersionChange}
          onThumbnailClick={onThumbnailClick}
        />

        {/* Section Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <ul className="space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <button
                    onClick={() => {
                      onSectionChange(section.id);
                      onClose();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-rune/20 text-rune border border-rune/30'
                        : 'text-mist hover:bg-stone/30 hover:text-parchment'
                    }`}
                  >
                    <Icon size={18} className="opacity-70" />
                    {section.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer with address */}
        <div className="p-3 border-t border-stone/30 text-xs text-mist/60">
          <div className="truncate" title={world.inboundAddress}>
            {world.inboundAddress}
          </div>
        </div>
      </aside>
    </>
  );
}
